import React from 'react';
import { MoreVertical } from 'lucide-react';
import { Wedding } from '../../types';
import { getRoleLabel } from '../../lib/weddings/labels';

export interface WeddingRowCardProps {
  wedding: Wedding;
  myRole?: string;
  myAccess?: 'owner' | 'admin' | 'coordinator' | 'viewer';
  onOpen: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
}

export const WeddingRowCard: React.FC<WeddingRowCardProps> = ({
  wedding,
  myRole,
  myAccess,
  onOpen,
  onEdit,
  onDuplicate,
  onArchive,
  onDelete,
}) => {
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr || dateStr.trim() === '') {
      return 'Bez data';
    }
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return 'Bez data';
      }
      return date.toLocaleDateString('cs-CZ', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Bez data';
    }
  };

  const hasActions = onEdit || onDuplicate || onArchive || onDelete;
  const [showMenu, setShowMenu] = React.useState(false);

  return (
    <div 
      className="bg-white dark:bg-slate-800 rounded-2xl border border-ds-border p-5 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors cursor-pointer group"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="text-ds-h3 text-slate-800 dark:text-slate-100 truncate">
              {wedding.title}
            </h3>
            <span className="text-ds-meta text-slate-400 dark:text-slate-500 whitespace-nowrap">
              {formatDate(wedding.date)}
            </span>
          </div>

          <p className="text-ds-body text-slate-500 dark:text-slate-400 mb-3">
            {wedding.location || 'Lokalita neurčena'}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {myRole && (
              <span className="text-ds-meta text-slate-400">{getRoleLabel(myRole)}</span>
            )}
          </div>
        </div>

        <div 
          className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          {hasActions && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className="p-2 text-slate-300 hover:text-slate-500 dark:hover:text-slate-400 transition-colors"
                aria-label="Další akce"
              >
                <MoreVertical size={16} />
              </button>
              
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  
                  <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-lg z-20 overflow-hidden border border-slate-100 dark:border-slate-700">
                    <div className="py-1">
                      {onEdit && (
                        <button
                          onClick={() => {
                            onEdit();
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-ds-body text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          Upravit
                        </button>
                      )}
                      {onDuplicate && (
                        <button
                          onClick={() => {
                            onDuplicate();
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-ds-body text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          Duplikovat
                        </button>
                      )}
                      {onArchive && (
                        <button
                          onClick={() => {
                            onArchive();
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-ds-body text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          Archivovat
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => {
                            onDelete();
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-ds-body text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          Smazat
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
